import { AddProjectTransactionForm } from '@/components/Payouts/admin/AddProjectTransactionForm';
import { Button, FormikTextInput, Modal } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';

import { CreateProjectTransactionPayload, IsoDateString, PayeeType } from 'types';

export const projectTransactionSchema = yup.object({
  amount: yup
    .number()
    // Inert here, where the field starts at 0, but the combined modal starts it empty so an
    // unfilled amount would otherwise report yup's raw cast failure.
    .typeError('Amount must be a number.')
    .required('Amount is a required field.')
    .test('isNonZero', 'Amount must not be zero', (value) => value !== 0),
  projectExternalId: yup.string().required('Account ID is a required field.'),
  event: yup.string().required('Payout Event is a required field.'),
  description: yup.string().optional().nullable(),
  clawbackRuleName: yup.string().nullable().optional(),
});

export const AddProjectTransactionModal = ({
  isOpen,
  handleClose,
  handleSubmit,
  modalTitle,
  submitButtonText,
  modalId,
  batchDate,
  payeeType,
  accountId,
  hideAccountSelection,
  allowHistoricalAdjustment = false,
  showFincoEPCOption = false,
}: {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (values: CreateProjectTransactionPayload) => Promise<void>;
  modalTitle: string;
  submitButtonText: string;
  modalId: string;
  payeeType?: PayeeType;
  batchDate?: IsoDateString;
  accountId?: string;
  hideAccountSelection?: boolean;
  allowHistoricalAdjustment?: boolean;
  showFincoEPCOption?: boolean;
}) => {
  const initialValues: CreateProjectTransactionPayload = useMemo(() => {
    return {
      projectExternalId: accountId ?? '', //accountId
      amount: 0,
      prePayment: false,
      event: '' as unknown as undefined,
      description: '',
      clawbackRuleName: undefined,
    };
  }, []);

  const onSubmit = useCallback(
    async (
      values: CreateProjectTransactionPayload,
      { setSubmitting, resetForm }: FormikHelpers<CreateProjectTransactionPayload>,
    ) => {
      await handleSubmit({ ...values, batchDate });
      setSubmitting(false);
      resetForm();
    },
    [],
  );

  return (
    <Formik
      validationSchema={projectTransactionSchema}
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize={true}
      validateOnBlur
    >
      {({ isSubmitting, resetForm, submitForm, values }) => (
        <Form>
          <Modal
            isOpen={isOpen}
            maxWidth="4xl"
            ariaLabelledBy={modalId}
            onDismiss={() => {
              if (!isSubmitting) {
                resetForm();
                handleClose();
              }
            }}
          >
            <Modal.Header
              id={modalId}
              title={modalTitle}
              onDismiss={() => {
                if (!isSubmitting) {
                  resetForm();
                  handleClose();
                }
              }}
            />
            <Modal.Body background="secondary" childGap="lg">
              {!hideAccountSelection && (
                <Field
                  type="text"
                  label="Account ID"
                  name="projectExternalId"
                  id="projectExternalId"
                  component={FormikTextInput}
                  isRequired={true}
                />
              )}
              {values.projectExternalId?.length === 24 && (
                <AddProjectTransactionForm
                  payeeType={payeeType}
                  hideAccountSelection={hideAccountSelection}
                  allowHistoricalAdjustment={allowHistoricalAdjustment}
                  showFincoEPCOption={showFincoEPCOption}
                />
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                tone="neutral"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                onClick={() => {
                  resetForm();
                  handleClose();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                type="submit"
                onClick={submitForm}
              >
                {submitButtonText}
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};
